// frontend/src/services/email/emailAccountService.ts
// Service adapter to connect existing email UI to new encrypted backend

import { toast } from 'react-toastify';

import { handleApiError } from '../../utils/apiErrorHandler';
import apiClient from '../apiClient';
import { EmailAccount, EmailProvider } from './types';

export interface BackendEmailAccount {
  _id: string;
  tenantId: string;
  userId: string;
  provider: EmailProvider;
  emailAddress: string;
  displayName?: string;
  authType: 'oauth' | 'imap_smtp';
  status: 'connected' | 'disconnected' | 'error' | 'token_expired';
  connectionState?:
    | 'DISCONNECTED'
    | 'CONNECTING'
    | 'CONNECTED_NO_SYNC'
    | 'SYNCING'
    | 'READY'
    | 'ERROR';
  tokenExpiresAt?: string;
  lastSyncAt?: string;
  isDefault?: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONNECTION STATUS TYPES (Enterprise-Grade UX)
// ═══════════════════════════════════════════════════════════════════════════

export type ConnectionState =
  | 'DISCONNECTED'
  | 'CONNECTING'
  | 'CONNECTED_NO_SYNC'
  | 'SYNCING'
  | 'READY'
  | 'ERROR';
export type OverallStatus = 'NO_ACCOUNTS' | 'NEEDS_SYNC' | 'SYNCING' | 'READY' | 'ERROR';

export interface AccountConnectionStatus {
  state: ConnectionState;
  indicator: 'green' | 'yellow' | 'red';
  message: string;
  action: 'reconnect' | 'sync' | 'refresh' | null;
  actionLabel: string | null;
  email: string;
  provider: string;
  providerDisplayName: string;
  lastSyncAt: string | null;
  lastSyncFormatted: string;
  emailCount: number;
  isReady: boolean;
  needsSync: boolean;
  hasError: boolean;
}

export interface ConnectionStatusResponse {
  success: boolean;
  hasAccounts: boolean;
  status: OverallStatus;
  message: string;
  action: 'connect' | 'sync' | 'refresh' | 'fix' | 'reconnect' | null;
  actionLabel: string | null;
  accounts: AccountConnectionStatus[];
  summary?: {
    total: number;
    ready: number;
    needsSync: number;
    errors: number;
  };
}

export interface CreateAccountPayload {
  provider: EmailProvider;
  emailAddress: string;
  displayName?: string;
  authType: 'oauth' | 'imap_smtp';
  tokens: any;
  tokenExpiresAt?: string | number;
  meta?: Record<string, any>;
  isDefault?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONNECTION STATUS API (Enterprise-Grade UX)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fetch connection status for all email accounts
 * This is the TRUTH SOURCE for UI state
 */
export async function fetchConnectionStatus(): Promise<ConnectionStatusResponse> {
  try {
    const response = await apiClient.get<ConnectionStatusResponse>('/api/email-accounts/status');
    return response;
  } catch (error) {
    console.error('Failed to fetch connection status:', error);
    // Return a safe default
    return {
      success: false,
      hasAccounts: false,
      status: 'ERROR',
      message: 'Failed to check email connection status',
      action: 'connect',
      actionLabel: 'Connect Email',
      accounts: [],
    };
  }
}

/**
 * Trigger sync for a specific account
 */
export async function triggerAccountSync(
  accountId: string
): Promise<{ success: boolean; jobId?: string; message?: string }> {
  try {
    console.log('🔄 Triggering sync for account:', accountId);
    const response = await apiClient.post<{ success: boolean; jobId?: string; message?: string }>(
      `/api/email-accounts/${accountId}/sync`
    );
    return { success: response.success, jobId: response.jobId, message: response.message };
  } catch (error) {
    console.error('Failed to trigger sync:', error);
    handleApiError(error, 'Failed to start email sync');
    return { success: false };
  }
}

/**
 * Fetch all email accounts from encrypted backend
 */
export async function fetchEmailAccounts(): Promise<EmailAccount[]> {
  try {
    const response = await apiClient.get<{
      success: boolean;
      accounts: BackendEmailAccount[];
      count: number;
    }>('/api/email-accounts');

    if (!response.success || !response.accounts) {
      console.warn('No email accounts found');
      return [];
    }

    return response.accounts.map(transformBackendAccount);
  } catch (error) {
    console.error('Failed to fetch email accounts:', error);
    handleApiError(error, 'Failed to load email accounts', { silent: true }); // Silent since this is a background fetch
    return [];
  }
}

/**
 * Create or update email account in encrypted backend
 */
export async function saveEmailAccount(
  payload: CreateAccountPayload
): Promise<EmailAccount | null> {
  try {
    console.log('💾 Saving email account:', payload.emailAddress);

    const response = await apiClient.post<{ success: boolean; account: BackendEmailAccount }>(
      '/api/email-accounts',
      payload
    );

    if (!response.success || !response.account) {
      throw new Error('Failed to save email account');
    }

    console.log('✅ Email account saved to encrypted backend:', response.account.emailAddress);
    toast.success(`Email account ${response.account.emailAddress} connected successfully!`);

    return transformBackendAccount(response.account);
  } catch (error) {
    console.error('Failed to save email account:', error);
    handleApiError(error, 'Failed to connect email account');
    return null;
  }
}

/**
 * Delete email account (disconnect and revoke)
 */
export async function deleteEmailAccount(accountId: string): Promise<boolean> {
  try {
    console.log('🗑️  Disconnecting email account:', accountId);

    const response = await apiClient.delete<{ success: boolean }>(
      `/api/email-accounts/${accountId}`
    );

    if (!response.success) {
      throw new Error('Failed to delete email account');
    }

    console.log('✅ Email account disconnected');
    toast.success('Email account disconnected successfully!');
    return true;
  } catch (error) {
    console.error('Failed to delete email account:', error);
    handleApiError(error, 'Failed to disconnect email account');
    return false;
  }
}

/**
 * Refresh OAuth tokens for an account
 */
export async function refreshAccountTokens(accountId: string): Promise<EmailAccount | null> {
  try {
    console.log('🔄 Refreshing tokens for account:', accountId);

    const response = await apiClient.post<{ success: boolean; account: BackendEmailAccount }>(
      `/api/email-accounts/${accountId}/refresh`
    );

    if (!response.success || !response.account) {
      throw new Error('Failed to refresh tokens');
    }

    console.log('✅ Tokens refreshed for account');
    toast.success('Email account tokens refreshed successfully!');

    return transformBackendAccount(response.account);
  } catch (error) {
    console.error('Failed to refresh tokens:', error);
    handleApiError(error, 'Failed to refresh email account tokens');
    return null;
  }
}

/**
 * Get decrypted tokens for API calls (backend handles decryption)
 * This is a helper that existing services can use
 */
export async function getAccountTokens(accountId: string): Promise<any | null> {
  try {
    // Backend will decrypt and return tokens when needed
    // For now, this is a placeholder for future enhancement
    // The actual token retrieval happens in backend when making Gmail/Outlook API calls

    console.log('Token retrieval is handled by backend for security');
    return null;
  } catch (error: any) {
    console.error('Failed to get account tokens:', error);
    return null;
  }
}

/**
 * Transform backend account format to frontend format
 */
function transformBackendAccount(backendAccount: BackendEmailAccount): EmailAccount {
  // Map backend provider names to frontend EmailProvider type
  const providerMap: Record<string, EmailProvider> = {
    google: 'gmail',
    microsoft: 'outlook',
    apple: 'apple',
    yahoo: 'yahoo',
    exchange: 'exchange',
    custom: 'custom',
  };

  return {
    id: backendAccount._id,
    provider: providerMap[backendAccount.provider] || 'gmail',
    email: backendAccount.emailAddress,
    name: backendAccount.displayName || backendAccount.emailAddress,
    isConnected: backendAccount.status === 'connected',
    isDefault: backendAccount.isDefault || false,
  };
}

/**
 * Check if migration from localStorage is needed
 */
export default {
  fetchEmailAccounts,
  saveEmailAccount,
  deleteEmailAccount,
  refreshAccountTokens,
  getAccountTokens,
  fetchConnectionStatus,
  triggerAccountSync,
};
